import React from 'react';
import { motion } from 'framer-motion';
import { Mail } from 'lucide-react';
import { mascot } from '../../data/mock';
import { useTranslation } from 'react-i18next';
import { STORE_LINKS } from '../../constants/links';

const Footer = () => {
  const { t } = useTranslation();
  const footerLinks = {
    producto: [
      { name: t('footer.howItWorks'), href: '#how-it-works' },
      { name: t('footer.dailyPlans'), href: '#trip-planner' },
      { name: t('footer.pricing'), href: '#pricing' }
    ],
    empresa: [
      { name: t('footer.contact'), href: 'mailto:apekaistudios@gmail.com' }
    ],
    legal: [
      { name: t('footer.privacy'), href: '/privacidad.html', target: '_blank' },
      { name: t('footer.terms'), href: '/terminos.html', target: '_blank' },
      { name: t('footer.cookies'), href: '#' }
    ]
  };

  const socialLinks = [
    { icon: Mail, href: 'mailto:apekaistudios@gmail.com', label: 'Email' }
  ];

  return (
    <footer className="bg-white text-slate-900 relative overflow-hidden border-t border-slate-200">
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-12">
          
          {/* Brand Column with Mascot */}
          <motion.div
            className="col-span-2 md:col-span-3 lg:col-span-2"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <a href="/" className="flex items-center gap-3 mb-6 group">
              <motion.div
                className="w-12 h-12 rounded-xl overflow-hidden border border-slate-200 p-1.5 bg-[#FAF9F6]"
                whileHover={{ scale: 1.05, rotate: [0, -5, 5, 0] }}
                transition={{ duration: 0.5 }}
              >
                <img
                  src={mascot.main}
                  alt="APEKAI Places Mascot"
                  className="w-full h-full object-contain"
                />
              </motion.div>
              <span className="text-base font-black tracking-tight text-slate-950">APEKAI Places</span>
            </a>
            <p className="text-slate-400 text-lg leading-relaxed max-w-sm">
              {t('footer.tagline')}
            </p>
            <div className="flex gap-3">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={social.label}
                  href={social.href}
                  className="w-9 h-9 rounded-xl bg-slate-100 hover:bg-slate-900 hover:text-white flex items-center justify-center text-slate-800 transition-all duration-300 border border-slate-200/50"
                  aria-label={social.label}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 * index, type: "spring", stiffness: 200 }}
                  whileHover={{ scale: 1.05, y: -1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <social.icon className="w-4 h-4" />
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Links Columns */}
          {[
            { title: t('footer.product'), links: footerLinks.producto },
            { title: t('footer.company'), links: footerLinks.empresa },
            { title: t('footer.legal'), links: footerLinks.legal }
          ].map((section, sectionIndex) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 * sectionIndex, duration: 0.5 }}
            >
              <h4 className="text-xs font-black uppercase tracking-widest text-slate-950 mb-4">{section.title}</h4>
              <ul className="space-y-3">
                {section.links.map((link, linkIndex) => (
                  <motion.li
                    key={link.name}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.05 * linkIndex + 0.1 * sectionIndex }}
                  >
                    <a
                      href={link.href}
                      target={link.target}
                      rel={link.target === '_blank' ? 'noopener noreferrer' : undefined}
                      className="text-slate-500 hover:text-slate-950 transition-colors text-xs font-semibold relative group inline-block"
                    >
                      {link.name}
                      <span className="absolute -bottom-0.5 left-0 w-0 h-[1.5px] bg-slate-900 group-hover:w-full transition-all duration-300" />
                    </a>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Bottom Bar */}
        <motion.div
          className="mt-16 pt-8 border-t border-slate-200 flex flex-col md:flex-row justify-between items-center gap-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
        >
          <p className="text-slate-400 text-[10px] font-semibold">
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </p>
          <div className="flex items-center gap-6">
            {[
              { href: STORE_LINKS.ios, icon: 'apple', text: 'App Store' },
              { href: STORE_LINKS.android, icon: 'google', text: 'Google Play' }
            ].map((store, index) => (
              <motion.a
                key={store.icon}
                href={store.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors text-[10px] font-black uppercase tracking-wider group"
                whileHover={{ x: 2 }}
              >
                {store.icon === 'apple' ? (
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.5,12.92 20.16,13.19L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                  </svg>
                )}
                {store.text}
              </motion.a>
            ))}
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
